// src/view/products/components/SellButton.tsx
"use client";

import {
  Archive,
  Bell,
  BellRing,
  Loader2,
  Minus,
  Plus,
  Trash2,
} from "lucide-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { showToast } from "@/src/components/toast/showToast";
import { get, handleErrors, post } from "@/src/api/apiHandler";
import MyButton from "@/src/screen/ui/MyButton";
import useCartStore from "@/src/store/useCartStore";
import useLoginStore from "@/src/store/useLoginStore";
import useUserStore from "@/src/store/useUser";
import { PersianNum } from "@/src/tools";
import { useRouter } from "next/navigation";
import FavoriteButton from "./favorite/FavoriteButton";

interface SellButtonProps {
  ProductId: number;
  ProductIdentityId: number;
  unitPrice: number;
  unitDiscount: number;
  countWarehouse: number;
  available: boolean;
  property?: { attr: string; property: string }[];
  loading?: boolean;
}

const SellButton: FC<SellButtonProps> = ({
  ProductId,
  ProductIdentityId,
  unitPrice,
  unitDiscount,
  countWarehouse,
  available,
  property = [],
  loading = false,
}) => {
  const { addCard, removeFromCart, increaseCard, decreaseCard, cart } =
    useCartStore();
  const { dataUser } = useUserStore();
  const { openLogin } = useLoginStore();
  const router = useRouter()
  const cartItem = cart.find((c) => c.ProductIdentityId === ProductIdentityId);
  const count = cartItem?.count ?? 0;
  const isInCart = count > 0;
  const canIncrease = count < countWarehouse;

  /* ---------- انتخاب موقت قبل از افزودن ---------- */
  const [tempCount, setTempCount] = useState(1);
  const canIncreaseTemp = tempCount < countWarehouse;

  /* ---------- وضعیت یادآوری محصول ---------- */
  const [isReminderActive, setIsReminderActive] = useState(false);
  const [loadingRemember, setLoadingRemember] = useState(false);

  /* ---------- API: وضعیت فعلی یادآوری ---------- */
  const fetchReminderStatus = useCallback(async () => {
    try {
      setLoadingRemember(true);
      const { data } = await get(
        `/remember-product?prod=${ProductIdentityId}`,
      ); // boolean
      setIsReminderActive(Boolean(data));
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoadingRemember(false);
    }
  }, [ProductIdentityId]);

  /* ---------- API: فعال/غیرفعال کردن یادآوری ---------- */
  const toggleReminder = useCallback(async () => {
    if (!dataUser?.token) {
      openLogin();
      return;
    }

    try {
      setLoadingRemember(true);
      const { data } = await post(`/remember-product`, {
        ProductId,
        ProductIdentityId,
      }); // { active: boolean }

      setIsReminderActive(Boolean(data.active));

      if (data.active) {
        showToast(
          "یادآوری فعال شد؛ به محض موجود شدن، اس‌ام‌اس ارسال می‌شود.",
          "success",
        );
      } else {
        showToast("یادآوری غیرفعال شد.", "info");
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoadingRemember(false);
    }
  }, [ProductId, ProductIdentityId, dataUser?.token, openLogin]);

  /* ---------- افکت نخست ---------- */
  useEffect(() => {
    if (!available && dataUser?.token) fetchReminderStatus();
  }, [available, dataUser?.token, fetchReminderStatus]);

  /* ---------- هندل افزودن/حذف از سبد ---------- */
  const handleAddOrRemoveMain = useCallback(() => {
    if (!available) return;

    if (!isInCart) {
      if (countWarehouse === 0) {
        showToast("موجودی تمام شد", "warning");
        return;
      }
      addCard({
        ProductId,
        ProductIdentityId,
        count: tempCount,
        unitPrice,
        unitDiscount,
        property
      });
      setTempCount(1);
      showToast("به سبد اضافه شد", "success");
    } else {
      router.push("/order/check");
    }
  }, [
    available,
    isInCart,
    countWarehouse,
    addCard,
    removeFromCart,
    ProductId,
    ProductIdentityId,
    unitPrice,
    unitDiscount,
    property,
    tempCount,
  ]);

  /* ---------- افزایش/کاهش بعد از ورود ---------- */
  const handleIncrease = () => {
    if (canIncrease) increaseCard(ProductIdentityId);
    else showToast("موجودی کافی نیست", "warning");
  };

  const handleDecrease = () => {
    if (count > 1) decreaseCard(ProductIdentityId);
    else {
      removeFromCart(ProductIdentityId);
      showToast("از سبد حذف شد", "warning");
    }
  };

  /* ---------- افزایش/کاهش موقت ---------- */
  const handleTempIncrease = () => {
    if (canIncreaseTemp) setTempCount((v) => v + 1);
    else showToast("موجودی کافی نیست", "warning");
  };
  const handleTempDecrease = () => {
    if (tempCount > 1) setTempCount((v) => v - 1);
  };

  /* ---------- متن‌ها و آیکون ---------- */
  const getNotifyTexts = useMemo(() => {
    if (loading || loadingRemember) {
      return { text: "", aria: "در حال پردازش", Icon: Loader2 };
    }

    if (!available) {
      return isReminderActive
        ? {
          text: "منتظر پیام ما باش",
          aria: "یادآوری فعال است",
          Icon: BellRing,
        }
        : {
          text: "موجود شد، به من بگو",
          aria: "موجود شد، به من بگو",
          Icon: Bell,
        };
    }

    if (isInCart) {
      return { text: "مشاهده سبد خرید", aria: "حذف از سبد", Icon: Archive };
    }

    return { text: "افزودن به سبد", aria: "افزودن به سبد", Icon: Archive };
  }, [
    available,
    isReminderActive,
    isInCart,
    loading,
    loadingRemember,
  ]);

  const isDisabled =
    loading ||
    loadingRemember ||
    (available ? countWarehouse === 0 : false);
  const primaryAction = available ? handleAddOrRemoveMain : toggleReminder;

  return (
    <div>
      {/* ---- انتخاب تعداد، فقط وقتی محصول موجود است ---- */}
      {available && !loading && !loadingRemember && (
        <>
          {!isInCart && (
            <div
              role="group"
              aria-label="تعیین تعداد خرید محصول"
              className="flex items-center justify-between max-w-9 bg-white border border-[#F5F5F7] rounded-md shadow-sm"
            >
              <button
                type="button"
                onClick={handleTempDecrease}
                aria-label="کاهش تعداد"
                className="p-2 hover:bg-[#F5F5F7] transition"
              >
                <Minus size={16} className="text-[#606060]" />
              </button>
              <span className="px-3 font-medium text-[#2E2E2E]">
                {PersianNum(tempCount)}
              </span>
              <button
                type="button"
                onClick={handleTempIncrease}
                disabled={!canIncreaseTemp}
                aria-label="افزایش تعداد"
                className="p-2 hover:bg-[#F5F5F7] transition disabled:opacity-40"
              >
                <Plus size={16} className="text-[#606060]" />
              </button>
            </div>
          )}

          {isInCart && (
            <div
              role="group"
              aria-label="تغییر تعداد محصول در سبد خرید"
              className="flex items-center justify-between bg-white border border-[#F5F5F7] rounded-md shadow-sm"
            >
              <button
                type="button"
                onClick={handleDecrease}
                aria-label={count === 1 ? "حذف محصول از سبد" : "کاهش تعداد"}
                className="p-2 hover:bg-[#F5F5F7] transition"
              >
                {count === 1 ? (
                  <Trash2 size={16} className="text-red-500" />
                ) : (
                  <Minus size={16} className="text-[#606060]" />
                )}
              </button>
              <span className="px-3 font-medium text-[#2E2E2E]">
                {PersianNum(count)}
              </span>
              <button
                type="button"
                onClick={handleIncrease}
                disabled={!canIncrease}
                aria-label="افزایش تعداد"
                className="p-2 hover:bg-[#F5F5F7] transition disabled:opacity-40"
              >
                <Plus size={16} className="text-[#606060]" />
              </button>
            </div>
          )}
        </>
      )}

      {/* ---- دکمه اصلی ---- */}
      <div className="flex items-center">
        <MyButton
          type="button"
          onClick={primaryAction}
          disabled={isDisabled}
          color={
            !available ? "warning" : isInCart ? "primary" : "success"
          }
          variant="solid"
          isLoading={loading || loadingRemember}
          aria-label={getNotifyTexts.aria}
          className="flex-1 flex items-center justify-center gap-2 mt-2 font-bold text-xs"
        >
          <getNotifyTexts.Icon
            className={
              loading || loadingRemember ? "animate-spin h-4 w-4" : "size-5"
            }
          />
          <span>{getNotifyTexts.text}</span>
        </MyButton>
        <FavoriteButton prodId={ProductId} />
      </div>
    </div>
  );
};

export default SellButton;
